import React from "react";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import { get, map, filter } from "lodash";

import { fetchGet } from "../../api/fetch";

import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import RadioGroup from "@material-ui/core/RadioGroup";
import Radio from "@material-ui/core/Radio";

import TableCellStyled from "../Common/TableCellStyled";
import EventsDialog from "../Events/EventsDialog";

import { formatDate } from "../../utils/date";
import { displayColon } from "../../utils/data";
import { getUser } from "../../utils/users";
import { getLov } from "../../utils/lov";
import { getLovValue } from "../../utils/profile";
import { getLukeActionColor } from "../../utils/eventGroups";

import { driver, vehicle, trailer } from "../../constants/common";
import { driveInOut, walkInOut } from "../../constants/reducers";

import colors from "../../assets/style/colors";
import images from "../../assets/img/images";

const styles = {
  cardTitle: {
    backgroundColor: colors.primary40,
    marginBottom: "10px",
  },
  headerCell: {
    borderRight: "3px solid white",
    backgroundColor: colors.cardTitleBg,
    color: colors.black87,
    fontWeight: 500,
    height: "50px",
  },
  radio1: {
    color: colors.black54,
    fontSize: "1em !important",
    marginRight: "40px !important",
  },
  radio2: {
    color: colors.black54,
    fontSize: "1em !important",
  },
  iconItem: {
    width: "30px",
    margin: "0px 5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  infoItem: {
    display: "flex",
    alignItems: "center",
    "& span": {
      paddingRight: "5px",
    },
  },
};

const viewModes = {
  CHRONO: "CHRONO",
  PROFILE_TYPE: "PROFILE_TYPE",
};

const Timeline = (props) => {
  const { classes, language } = props;
  const [state, setState] = React.useState({});
  const [lov, setLov] = React.useState({
    105: [],
    158: [],
  });
  const [viewMode, setViewMode] = React.useState(viewModes.CHRONO);

  React.useEffect(() => {
    const fetchLov = async () => {
      setLov({
        105: await getLov(105),
        158: await getLov(158),
      });
    };
    fetchLov();
  }, []);

  // Remove events that shouldn't be displayed
  const data = props.data
    ? props.data.filter(
        (row) => ![7, 49, 81].includes(get(row, ["type", "id"]))
      )
    : [];

  React.useEffect(() => {
    const fetchOverrideInfo = async () => {
      let newState = {
        users: {},
        eventGroups: {},
      };
      for (let i = 0; i < data.length; i++) {
        let item = data[i];
        if (get(item, ["type", "id"]) === 64) {
          if (!newState.users[get(item, ["user_id"])]) {
            newState.users[get(item, ["user_id"])] = await getUser(
              get(item, ["user_id"])
            );
          }
          if (!newState.eventGroups[get(item, ["id"])]) {
            newState.eventGroups[get(item, ["id"])] = await fetchGet(
              `event_groups/${get(item, ["id"])}`
            );
          }
        }
      }
      setState(newState);
    };
    if (data) {
      fetchOverrideInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data]);

  const renderEvent = (row) => {
    let icon1, icon2;
    let text = get(row, ["type", `name_${language}`]);

    // Detection Event
    if ([1, 65, 67].includes(get(row, ["type", "id"]))) {
      icon1 =
        get(row, ["type", "id"]) === 1
          ? images.humanIconBlack
          : get(row, ["type", "id"]) === 65
          ? images.truckIconBlack
          : get(row, ["type", "id"]) === 67
          ? images.trailerIconBlack
          : "";

      icon2 = images.cameraIcon;
      text = get(row, ["type", `name_${language}`]);
    }

    // Vehicle Full Stop
    if ([80].includes(get(row, ["type", "id"]))) {
      icon1 = images.truckIconBlack;
      icon2 = images.stopIcon;
      text = get(row, ["type", `name_${language}`]);
    }

    // Human/Vehicle/Trailer Exit
    if ([3, 66, 68].includes(get(row, ["type", "id"]))) {
      icon1 =
        get(row, ["type", "id"]) === 3
          ? images.humanIconBlack
          : get(row, ["type", "id"]) === 66
          ? images.truckIconBlack
          : images.trailerIconBlack;
      icon2 = images.exitIcon;
      text = get(row, ["type", `name_${language}`]);
    }

    // LPR/OCR Match Result
    if ([57, 63].includes(get(row, ["type", "id"]))) {
      let eventType = get(row, ["type", "id"]);
      let profileType = get(row, ["profile", "type", "id"]);
      let matched = get(row, ["recognized"]);
      icon1 =
        profileType === 2
          ? images.truckIconBlack
          : profileType === 3
          ? images.trailerIconBlack
          : "";
      icon2 =
        matched === true ? images.checkCircleColored : images.questionMark;
      text = `${
        profileType === 2
          ? I18n.get("site_truck")
          : profileType === 3
          ? I18n.get("site_trailer")
          : ""
      } ${eventType === 57 ? "LPR" : eventType === 63 ? "OCR" : ""} ${
        matched ? I18n.get("site_match") : I18n.get("site_unmatch")
      }`;
      if (matched) {
        text += ` (${get(row, ["profile", "name"])})`;
      }
    }

    // Face Result
    if ([5, 6].includes(get(row, ["type", "id"]))) {
      let matched = get(row, ["type", "id"]) === 5 ? true : false;
      icon1 = images.humanIconBlack;
      icon2 = matched ? images.faceMatched : images.faceUnMatched;
      text = `${I18n.get("site_face")} ${
        matched ? I18n.get("site_match") : I18n.get("site_unmatch")
      }`;
      if (matched) {
        text += ` (${get(row, ["profile", "first_name"])} ${get(row, [
          "profile",
          "last_name",
        ])})`;
      }
    }

    // Barcode Result
    if ([7, 8].includes(get(row, ["type", "id"]))) {
      let matched =
        get(row, ["type", "id"]) === 8
          ? get(row, ["recognized"]) === true
          : null;
      icon1 = images.humanIconBlack;
      icon2 = matched ? images.barcodeMatched : images.barcodeUnmatched;
      text = `${I18n.get("site_barcode")} ${
        matched === null
          ? I18n.get("site_request")
          : matched
          ? I18n.get("site_match")
          : I18n.get("site_unmatch")
      }`;
      if (matched && get(row, ["profile", "first_name"])) {
        text += ` (${get(row, ["profile", "first_name"])} ${get(row, [
          "profile",
          "last_name",
        ])})`;
      }
    }

    // Barcode Result
    if ([50].includes(get(row, ["type", "id"]))) {
      let matched = get(row, ["recognized"]) === true;
      icon1 = images.humanIconBlack;
      icon2 = matched ? images.rfidMatched : images.rfidUnmatched;
      text = `${I18n.get("site_rfid")} ${
        matched ? I18n.get("site_match") : I18n.get("site_unmatch")
      }`;
      if (matched && get(row, ["profile", "first_name"])) {
        text += ` (${get(row, ["profile", "first_name"])} ${get(row, [
          "profile",
          "last_name",
        ])})`;
      }
    }

    // Verification Start/Stop
    if ([85, 86].includes(get(row, ["type", "id"]))) {
      icon1 = images.humanIconBlack;
      icon2 = images.checklistIcon;
      text = get(row, ["type", `name_${language}`]);
    }

    // Luke Action
    if ([88].includes(get(row, ["type", "id"]))) {
      icon1 = images.humanIconBlack;
      icon2 = getLukeActionColor(get(row, ["luke_action_json"]));
      text = getLovValue(158, get(row, ["luke_action"]), lov, language);
      if (!text) return;
    }

    // Luke Decision
    if ([87].includes(get(row, ["type", "id"]))) {
      icon1 = images.humanIconBlack;
      icon2 =
        get(row, ["decision"]) === 43
          ? images.verificationIconOk
          : get(row, ["decision"]) === 44
          ? images.deniedIcon
          : get(row, ["decision"]) === 93
          ? images.requireAssistanceIcon
          : images.lukeTimeline;
      text = getLovValue(105, get(row, ["decision"]), lov, language);
    }

    // Verification Lookup
    if ([90].includes(get(row, ["type", "id"]))) {
      icon1 = images.humanIconBlack;
      icon2 = images.verificationLookup;
      text = `${get(row, ["type", `name_${language}`])} (${
        get(row, ["verification_result_found"])
          ? I18n.get("site_found")
          : I18n.get("site_notFound")
      })`;
    }

    // Last Verification Kiosk Confirmation
    if ([91].includes(get(row, ["type", "id"]))) {
      icon1 = images.humanIconBlack;
      icon2 = images.checklistIcon;
      text = `${I18n.get("site_lastVerification")} (${
        get(row, ["is_this_your_last_survey_results"])
          ? I18n.get("site_last")
          : I18n.get("site_notLast")
      }, 
      ${
        get(row, ["has_anything_changed"])
          ? I18n.get("site_changed")
          : I18n.get("site_noChanges")
      })`;
    }

    // Profile Override
    if ([64].includes(get(row, ["type", "id"]))) {
      let profileType = get(state, [
        "eventGroups",
        get(row, ["id"]),
        "type",
        "id",
      ]);
      icon1 =
        profileType === 10
          ? images.truckIconBlack
          : profileType === 11
          ? images.trailerIconBlack
          : profileType === 12
          ? images.containerIconBlack
          : profileType === 1
          ? images.humanIconBlack
          : "";
      icon2 = images.overrideIconBlack;
      text = `${
        profileType === 10
          ? I18n.get("site_truck")
          : profileType === 11
          ? I18n.get("site_trailer")
          : profileType === 12
          ? I18n.get("site_container")
          : profileType === 1
          ? I18n.get("site_human")
          : ""
      } ${I18n.get("site_override")} (${I18n.get("site_by")} ${get(state, [
        "users",
        get(row, ["user_id"]),
        "profile",
        "firstname",
      ])} ${get(state, [
        "users",
        get(row, ["user_id"]),
        "profile",
        "lastname",
      ])})`;
    }

    return renderEventCell(icon1, icon2, text);
  };

  const renderEventCell = (icon1, icon2, text) => {
    return (
      <Grid container alignItems="center" wrap="nowrap">
        <Grid item className={classes.iconItem}>
          {icon1 && (
            <img
              src={icon1}
              height="15px"
              style={{ maxWidth: "30px" }}
              alt="icon"
            />
          )}
        </Grid>
        <Grid item className={classes.iconItem}>
          {icon2 && icon2.includes("#") ? (
            <Box
              style={{
                width: "14px",
                height: "14px",
                backgroundColor: icon2,
                borderRadius: "2px",
              }}
            />
          ) : icon2 ? (
            <img
              src={icon2}
              style={{
                maxWidth: [
                  images.checkCircleColored,
                  images.errorIcon,
                  images.barcodeIconBlack,
                ].includes(icon2)
                  ? "15px"
                  : "",
              }}
              alt="icon"
            />
          ) : (
            <></>
          )}
        </Grid>
        <Grid item className="text-54" style={{ margin: "0px 5px" }}>
          {text}
        </Grid>
      </Grid>
    );
  };

  const renderChronoView = () => {
    let preGateNb = 0;
    let gateNb = 0;
    data.forEach((item, index) => {
      // Vehicle Full Stop
      if ([80].includes(get(item, ["type", "id"]))) {
        preGateNb = index + 1;
      }
      // Vehicle Exit
      if ([66].includes(get(item, ["type", "id"]))) {
        gateNb = index + 1 - preGateNb;
      }
    });

    let totalTimeAtGate;
    if (props.section === driveInOut) {
      totalTimeAtGate = get(data, [preGateNb + gateNb - 1, "delta"]);
      if (preGateNb !== 0) {
        totalTimeAtGate -= get(data, [preGateNb - 1, "delta"]);
      }
    } else if (props.section === walkInOut) {
      data.forEach((item, index) => {
        // Human Exit
        if ([3].includes(get(item, ["type", "id"]))) {
          totalTimeAtGate = get(item, ["delta"]);
        }
      });
      if (!totalTimeAtGate) {
        totalTimeAtGate = get(data, [data.length - 1, "delta"]);
      }
    }

    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCellStyled
              className={classes.headerCell}
              style={{ width: "63px" }}
            >
              <Box style={{ lineHeight: "1.2" }}>{I18n.get("site_chrono")}</Box>
              <Box
                className="text-54"
                style={{
                  fontSize: "0.775em",
                  lineHeight: "1.2",
                  fontWeight: "400",
                }}
              >
                {I18n.get("site_chronoFormat")}
              </Box>
            </TableCellStyled>
            <TableCellStyled className={classes.headerCell}>
              {I18n.get("site_events")}
            </TableCellStyled>
            {props.section === driveInOut && (
              <TableCellStyled
                className={classes.headerCell}
                style={{ width: "80px" }}
              >
                {I18n.get("site_stage")}
              </TableCellStyled>
            )}
            <TableCellStyled
              className={classes.headerCell}
              style={{ width: "100px" }}
            >
              {I18n.get("site_timestamp")}
            </TableCellStyled>
          </TableRow>
        </TableHead>
        <TableBody>
          {map(
            filter(
              data,
              (row) =>
                get(row, ["type", "id"]) !== 88 || get(row, ["luke_action"])
            ),
            (row, index) => (
              <TableRow
                key={index}
                style={{
                  backgroundColor:
                    index % 2 || props.section === driveInOut
                      ? colors.white
                      : colors.tableDarkBg,
                }}
              >
                <TableCellStyled
                  className="text-54"
                  style={{
                    borderBottom: [
                      preGateNb - 1,
                      preGateNb + gateNb - 1,
                    ].includes(index)
                      ? `1px solid ${colors.veryLighPink}`
                      : "none",
                  }}
                >
                  {formatDate(new Date(Math.round(row.delta) * 1000), "mm:ss")}
                </TableCellStyled>
                <TableCellStyled
                  style={{
                    borderBottom: [
                      preGateNb - 1,
                      preGateNb + gateNb - 1,
                    ].includes(index)
                      ? `1px solid ${colors.veryLighPink}`
                      : "none",
                  }}
                >
                  {renderEvent(row)}
                </TableCellStyled>
                {props.section === driveInOut &&
                  [0, preGateNb, preGateNb + gateNb].includes(index) && (
                    <TableCellStyled
                      align="left"
                      rowSpan={
                        index === 0 && preGateNb !== 0
                          ? preGateNb
                          : index === preGateNb && gateNb !== 0
                          ? gateNb
                          : data.length - preGateNb - gateNb
                      }
                      className={classes.borderRight}
                      style={{
                        borderBottom: `1px solid ${colors.veryLighPink}`,
                      }}
                    >
                      <Box component="span" className="text-87 bold-500">
                        {index === 0 && preGateNb !== 0
                          ? I18n.get("site_preGate")
                          : index === preGateNb
                          ? I18n.get("site_atGate")
                          : I18n.get("site_postGate")}
                      </Box>
                    </TableCellStyled>
                  )}
                <TableCellStyled
                  className="text-54"
                  style={{
                    borderBottom: [
                      preGateNb - 1,
                      preGateNb + gateNb - 1,
                    ].includes(index)
                      ? `1px solid ${colors.veryLighPink}`
                      : "none",
                  }}
                >
                  {formatDate(row.timestamp, "hh:mm:ss A")}
                </TableCellStyled>
              </TableRow>
            )
          )}
          <TableRow style={{ borderTop: `1px solid ${colors.veryLighPink}` }}>
            <TableCellStyled
              colSpan={4}
              style={{ height: "60px", padding: "20px", borderBottom: "none" }}
            >
              <Box component="span" className="text-87 bold-500">
                {I18n.get(
                  props.section === driveInOut
                    ? "site_totalTimeAtGate"
                    : "site_totalTimeAtDoor"
                ).toUpperCase() + " "}
                {formatDate(
                  new Date(Math.round(totalTimeAtGate) * 1000),
                  "mm:ss"
                )}
              </Box>
            </TableCellStyled>
          </TableRow>
        </TableBody>
      </Table>
    );
  };

  const renderProfileTypeView = () => {
    let arrangedData = {
      vehicle: {
        name: I18n.get("site_vehicle"),
        image: images.truckIconBlack,
        duration: 0,
        data: [],
      },
      trailer: {
        name: I18n.get("site_trailer"),
        image: images.trailerIconBlack,
        duration: 0,
        data: [],
      },
      driver: {
        name: I18n.get("site_driver"),
        image: images.humanIconBlack,
        duration: 0,
        data: [],
      },
    };

    let fullStopDelta = 0;
    let exitDelta = 0;

    data.forEach((item, index) => {
      let eventType = get(item, ["type", "id"]);
      if (eventType === 80) {
        fullStopDelta = item.delta;
      }
      if (eventType === 66) {
        exitDelta = item.delta;
      }
      if (
        eventType === 65 ||
        eventType === 66 ||
        eventType === 80 ||
        (eventType === 57 && get(item, ["profile", "type", "id"]) === 2) ||
        (eventType === 63 && get(item, ["profile", "type", "id"]) === 2)
      ) {
        arrangedData.vehicle.data.push(item);
      }
      if (
        eventType === 67 ||
        eventType === 68 ||
        (eventType === 57 && get(item, ["profile", "type", "id"]) === 3) ||
        (eventType === 63 && get(item, ["profile", "type", "id"]) === 3)
      ) {
        arrangedData.trailer.data.push(item);
      }
      if ([1, 3, 5, 6].includes(eventType)) {
        arrangedData.driver.data.push(item);
      }
    });

    const calcDuration = (type) => {
      if (arrangedData[type].data.length > 0) {
        arrangedData[type].duration =
          arrangedData[type].data[arrangedData[type].data.length - 1].delta -
          arrangedData[type].data[0].delta;
      }
    };
    calcDuration(vehicle);
    calcDuration(trailer);
    calcDuration(driver);

    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCellStyled
              className={classes.headerCell}
              style={{ width: "130px" }}
            >
              {I18n.get("site_profileType")}
            </TableCellStyled>
            <TableCellStyled
              className={classes.headerCell}
              style={{ width: "80px" }}
            >
              <Box style={{ lineHeight: "1.2" }}>{I18n.get("site_chrono")}</Box>
              <Box
                className="text-54"
                style={{
                  fontSize: "0.775em",
                  lineHeight: "1.2",
                  fontWeight: "400",
                }}
              >
                {I18n.get("site_chronoFormat")}
              </Box>
            </TableCellStyled>
            <TableCellStyled className={classes.headerCell}>
              {I18n.get("site_events")}
            </TableCellStyled>
            <TableCellStyled
              className={classes.headerCell}
              style={{ width: "80px" }}
            >
              {I18n.get("site_stage")}
            </TableCellStyled>
            <TableCellStyled
              className={classes.headerCell}
              style={{ width: "100px" }}
            >
              {I18n.get("site_timestamp")}
            </TableCellStyled>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.keys(arrangedData).map((key) =>
            arrangedData[key].data.map((row, index) => (
              <TableRow key={index}>
                {index === 0 && (
                  <TableCellStyled
                    align="center"
                    rowSpan={arrangedData[key].data.length}
                    style={{
                      borderBottom: `1px solid ${colors.veryLighPink}`,
                    }}
                  >
                    <Grid container direction="column">
                      <Grid item>
                        <Grid container alignItems="center" justify="center">
                          <Grid item style={{ paddingRight: "10px" }}>
                            <img
                              src={arrangedData[key].image}
                              height="20px"
                              style={{ maxWidth: "40px" }}
                              alt="icon"
                            />
                          </Grid>
                          <Grid item className="text-87 bold-500">
                            {arrangedData[key].name}
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item className="text-54">
                        {`${I18n.get("site_duration")} ${formatDate(
                          new Date(
                            Math.round(arrangedData[key].duration) * 1000
                          ),
                          "mm:ss"
                        )}`}
                      </Grid>
                    </Grid>
                  </TableCellStyled>
                )}
                <TableCellStyled
                  className={`text-54 ${classes.borderRight}`}
                  style={{
                    borderBottom:
                      index === arrangedData[key].data.length - 1
                        ? `1px solid ${colors.veryLighPink}`
                        : "none",
                  }}
                >
                  {formatDate(new Date(Math.round(row.delta) * 1000), "mm:ss")}
                </TableCellStyled>
                <TableCellStyled
                  style={{
                    borderBottom:
                      index === arrangedData[key].data.length - 1
                        ? `1px solid ${colors.veryLighPink}`
                        : "none",
                  }}
                >
                  {renderEvent(row)}
                </TableCellStyled>
                <TableCellStyled
                  className={`text-54 ${classes.borderRight}`}
                  style={{
                    borderBottom:
                      index === arrangedData[key].data.length - 1
                        ? `1px solid ${colors.veryLighPink}`
                        : "none",
                  }}
                >
                  {row.delta <= fullStopDelta && fullStopDelta !== 0
                    ? I18n.get("site_preGate")
                    : row.delta <= exitDelta
                    ? I18n.get("site_atGate")
                    : I18n.get("site_postGate")}
                </TableCellStyled>
                <TableCellStyled
                  className="text-54"
                  style={{
                    borderBottom:
                      index === arrangedData[key].data.length - 1
                        ? `1px solid ${colors.veryLighPink}`
                        : "none",
                  }}
                >
                  {formatDate(row.timestamp, "hh:mm:ss A")}
                </TableCellStyled>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    );
  };

  let startEvent = get(props, ["log", "events"]);
  if (startEvent) {
    startEvent = startEvent.filter((e) =>
      [58, 59, 60, 61].includes(get(e, ["type", "id"]))
    )[0];
  }

  return (
    <Grid container direction="column" style={{ paddingTop: "8px" }}>
      <Grid item>
        <Grid container>
          <Grid item style={{ marginRight: "30px" }}>
            <Grid container direction="column">
              <Grid
                item
                className={classes.infoItem}
                style={{ marginBottom: "5px" }}
              >
                <span className="text-87 bold-500">
                  {I18n.get("site_company")}
                  {displayColon(language)}
                </span>
                <span className="text-54">
                  {get(startEvent, ["zone", "company", "name"])}
                </span>
              </Grid>
              <Grid item className={classes.infoItem}>
                <span className="text-87 bold-500">
                  {I18n.get("site_zone")}
                  {displayColon(language)}
                </span>
                <span className="text-54">
                  {get(startEvent, ["zone", "name"])}
                </span>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Grid container direction="column">
              <Grid
                item
                className={classes.infoItem}
                style={{ marginBottom: "5px" }}
              >
                <span className="text-87 bold-500">
                  {I18n.get("site_site")}
                </span>
                <span className="text-54">
                  {get(startEvent, ["zone", "site", "name"])}
                </span>
              </Grid>
              <Grid item className={classes.infoItem}>
                <span className="text-87 bold-500">
                  {I18n.get("site_device")}
                  {displayColon(language)}
                </span>
                <span className="text-54">
                  {get(startEvent, ["device", "name"])}
                </span>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item style={{ marginBottom: "5px", marginTop: "15px" }}>
        <Grid container alignItems="center">
          <Grid
            item
            className="text-87 bold-500"
            style={{ textTransform: "uppercase", marginRight: "20px" }}
          >
            {I18n.get("site_timeline")}
          </Grid>
          <Grid item xs style={{ marginLeft: "20px" }}>
            {props.section === driveInOut && (
              <RadioGroup
                row
                aria-label="position"
                name="position"
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
              >
                <FormControlLabel
                  value={viewModes.CHRONO}
                  control={<Radio color="primary" />}
                  label={I18n.get("site_chrono")}
                  labelPlacement="end"
                  classes={{ label: classes.radio1 }}
                  disabled={props.section === walkInOut}
                />
                <FormControlLabel
                  value={viewModes.PROFILE_TYPE}
                  control={<Radio color="primary" />}
                  label={I18n.get("site_byProfileType")}
                  labelPlacement="end"
                  classes={{ label: classes.radio2 }}
                  disabled={props.section === walkInOut}
                />
              </RadioGroup>
            )}
          </Grid>
          <Grid item className={classes.infoItem}>
            <EventsDialog
              eventGroupId={props.eventGroupId}
              disabled={props.blockLevel2}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        {data
          ? viewMode === viewModes.CHRONO
            ? renderChronoView()
            : renderProfileTypeView()
          : I18n.get("site_noData")}
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state) => {
  return {
    language: state.user.language,
    siteEntranceExit: state.siteEntranceExit,
  };
};

export default connect(mapStateToProps)(withStyles(styles)(Timeline));
